import type { de } from "./de.ts";

export const en: typeof de = {
  app: { name: "divespots.ch", srTitle: "divespots.ch: dive spots in Switzerland" },
  common: { close: "Close", loading: "Loading …", retry: "Try again" },
  diver: {
    title: "Account",
    nav: "Account navigation",
    nav_account: "My account",
    nav_suggestions: "My suggestions",
    signIn: "Sign in",
    signOut: "Sign out",
    menu: "Account of {{name}}",
    signOutFailed: "Signing out did not work. Please try again.",
    staffDirectTitle: "Edit directly",
    signOutOffline: "Signing out needs a connection.",
    staffDirect: "As a moderator you edit directly.",
    staffDirectLink: "To the admin area",
    needsConnection: "This page needs a connection.",
    login: {
      title: "Sign in",
      lead: "We email you a link. A passkey or a password works too.",
      registerTitle: "Create an account",
      registerLead: "Enter your email address. We send you a link that creates your account.",
      registerSend: "Create account",
      toRegister: "No account yet? Register",
      toSignIn: "Already have an account? Sign in",
      email: "Email",
      name: "Display name",
      nameHint: "Only for a new account, for example your first name. You can change it later.",
      send: "Send link by email",
      passkey: "Sign in with a passkey",
      sentTitle: "Check your inbox",
      sentText: "We sent a link to {{email}}. It is valid for 5 minutes. Open it on this device.",
      devHint: "Development: the link is printed in the API console.",
      otherEmail: "Use another address",
      badEmail: "Please enter a valid email address.",
      banned: "Account suspended.",
      password: "Sign in with a password",
      passwordLabel: "Password",
      passwordSubmit: "Sign in",
      wrongCredentials: "Email or password wrong",
      passwordMissing: "Please enter your email and password.",
      registrationClosed:
        "New registrations are currently closed. Existing accounts can still sign in.",
      rateLimited: "Too many attempts. Please wait a moment.",
      network: "No connection. Please try again.",
      failed: "That did not work. The link may have expired or be invalid.",
    },
    account: {
      title: "My account",
      profile: "Profile",
      name: "Display name",
      nameHint: "Shown with your suggestions.",
      nameInvalid: "Please enter 1 to 120 characters.",
      passkeys: "Passkeys",
      passkeyLead:
        "With a passkey you sign in without a link, using fingerprint, face or device code.",
      passkeySetup: "Set up a passkey",
      passkeyNone: "No passkey yet.",
      passkeyUnnamed: "Passkey",
      passkeyRemove: "Remove passkey {{name}}",
      passkeyAdded: "Passkey set up.",
      passkeyFailed: "The passkey could not be set up.",
      passkeyUnsupported: "This device or browser does not support passkeys.",
      offerTitle: "Set up a passkey?",
      offerText: "Next time you sign in with it directly, without email.",
      later: "Later",
      session: "Session",
      dangerTitle: "Delete account",
      dangerText:
        "Your account and sign-in data are deleted. Your suggestions and sighting reports stay anonymously.",
      delete: "Delete account",
      deleteTitle: "Really delete your account?",
      deleteBody:
        "This cannot be undone. Your suggestions stay under “Deleted account”, pending ones are withdrawn. Your sighting reports stay anonymously.",
      deleteConfirm: "Delete for good",
      deleteStaff: "Team accounts cannot delete themselves. An admin has to demote you first.",
    },
    suggestions: {
      title: "My suggestions",
      empty:
        "You have not suggested anything yet. Open a dive spot and tap “Suggest a change”, or suggest a new dive spot.",
    },
  },
  suggest: {
    lead: "Your suggestion is reviewed by the team before it becomes visible.",
    editLead:
      "Change what is wrong. Only the changed fields are suggested. The team reviews them before they become visible.",
    spotButton: "Suggest a dive spot",
    spotTitle: "Suggest a new dive spot",
    editSpotButton: "Suggest a change",
    editSpotTitle: "Suggest a change: {{name}}",
    objectButton: "Suggest an object",
    objectTitle: "Suggest an object: {{name}}",
    objectHere: "Suggest an object here",
    editObjectButton: "Suggest an object change",
    editObjectTitle: "Suggest an object change: {{name}}",
    loginHint: "Sign in to report sightings or suggest something",
    noteTitle: "Note for the team",
    note: "Note",
    noteHint: "Optional: how do you know?",
    noteHintEdit: "Required: what did you change and why?",
    send: "Send suggestion",
    thanks: "Thank you! Your suggestion is waiting for review.",
    err: {
      tooMany:
        "Too many open suggestions. Wait until the team has reviewed some, or withdraw some.",
      gone: "This no longer exists or is not public.",
      noteRequired: "Please enter a note.",
      nothingChanged: "You have not changed anything yet.",
    },
    status: {
      pending: "In review",
      approved: "Accepted",
      rejected: "Rejected",
      withdrawn: "Withdrawn",
    },
    kind: {
      spot_create: "New dive spot",
      object_create: "New object",
      spot_edit: "Change to a dive spot",
      object_edit: "Change to an object",
    },
    reason: "Reason",
    withdraw: "Withdraw",
    withdrawNamed: "Withdraw suggestion “{{name}}”",
    withdrawTitle: "Withdraw this suggestion?",
    withdrawBody: "The team will no longer see it.",
    more: "Load more",
    openSpot: "Open dive spot",
    openObject: "Open the dive spot with the object",
  },
  review: {
    title: "Suggestions",
    filterStatus: "Status",
    filterKind: "Kind",
    filterSubmitter: "Submitted by",
    empty: "No suggestions.",
    back: "Back to list",
    pendingCount_one: "{{count}} open suggestion",
    pendingCount_other: "{{count}} open suggestions",
    stale:
      "Stale: the target changed since the suggestion was made. Check the comparison carefully before you accept.",
    targetGone: "The target of this change no longer exists.",
    diff: "Comparison",
    field: "Field",
    before: "Current",
    after: "Suggestion",
    value: "Value",
    empty_value: "empty",
    approveTitle: "Accept",
    publish: "Publish right away (otherwise as a draft)",
    reason: "Reason",
    reasonHint: "Optional. The diver sees it.",
    adjust: "Adjust before accepting",
    force: "Accept anyway although stale",
    approve: "Accept",
    rejectTitle: "Reject",
    rejectHint: "Required. The diver sees the reason.",
    reject: "Reject",
    outcome: "Outcome",
    adjustedNote: "Adjusted by the team before it was accepted.",
    err: {
      conflict: "This changed in the meantime (stale or already handled). Reload the page.",
      badPayload: "The adjusted suggestion is invalid.",
      reasonRequired: "Please enter a reason.",
    },
    keys: {
      name: "Name",
      location: "Entry point",
      parkingLocation: "Parking",
      waterBody: "Water body",
      locality: "Place",
      difficulty: "Difficulty",
      maxDepthM: "Maximum depth (m)",
      closedPeriods: "Closed periods",
      ratings: "Ratings",
      description: "Description",
      access: "Access",
      parking: "Parking (text)",
      entry: "Entry (text)",
      hazards: "Hazards",
      restrictions: "Restrictions",
      facilities: "Facilities",
      type: "Type",
      depthOverrideM: "Measured depth",
      spotId: "Dive spot",
    },
  },
  sighting: {
    none: "No reports yet",
    lastSeen: "Last seen: {{date}}",
    lastMissing: "Last reported missing: {{date}}",
    count_one: "{{count}} report",
    count_other: "{{count}} reports",
    seenToday: "Seen today",
    seenOn: "Seen on {{date}}",
    gone: "Gone",
    seenNamed: "{{name}}: report seen today",
    goneNamed: "{{name}}: report gone",
    otherDay: "Another day (optional)",
    history: "History",
    statusSeen: "seen",
    statusGone: "missing",
    thanks: "Thank you for your report!",
    badDate: "The date is invalid (not in the future, at most 2 years back).",
    tooMany: "Too many reports. Please wait a moment.",
  },
  offline: {
    badge: "Offline",
    save: "Save offline",
    saved: "Saved offline",
    estimate: "Maps, 3D lakebed and aerial photo: about {{size}}.",
    confirm: "Save",
    cancel: "Cancel",
    saving: "Saving … {{percent}}%",
    progress: "Saving progress",
    remove: "Remove",
    removeNamed: "Remove {{name}} from offline storage",
    listTitle: "Saved offline",
    empty: "Nothing saved yet. Open a dive spot and tap “Save offline”.",
    open: "Open",
    total_one: "{{count}} dive spot · {{size}}",
    total_other: "{{count}} dive spots · {{size}}",
    unavailable: "Not available offline",
    unavailableText:
      "This dive spot was not saved. With a connection you can save it for offline use.",
    adminNeedsConnection: "Admin needs a connection",
    error: {
      quota: "Not enough storage on this device. Remove saved dive spots and try again.",
      network: "No connection. Saving was cancelled.",
      failed: "Saving did not work. Please try again.",
    },
  },
  pwa: { updateAvailable: "New version available", reload: "Reload" },
  minimap: {
    title: "Lake map",
    hide: "Hide lake map",
    show: "Show lake map",
    openSpot: "Go to dive spot {{name}}",
    object: "Object {{name}}",
    currentSpot: "{{name}}: current dive spot",
  },
  diveAccess: {
    chunkFailed: "Dive mode could not be loaded.",
    reload: "Reload",
    loadFailed: "Could not be loaded. Please try again.",
    button: "Dive mode (beta)",
    unavailableTitle: "Not available",
    unavailable: "This page is not available.",
    back: "Back to the map",
  },
  course: {
    same: "Same object",
    title: "Course",
    from: "Course from {{name}}",
    to: "to",
    pick: "Choose the destination.",
    pickPin: "Or tap a pin.",
    noOthers: "No other objects.",
    fromHere: "Course from here …",
    action: "Course",
    rowFrom: "Work out the course from {{name}}",
    rowTo: "Course from {{from}} to {{name}}",
    asDestination: "As destination",
    swap: "Swap start and destination",
    close: "Close course",
    computing: "Working it out …",
    unavailable: "Course not available",
    compass: "Compass",
    true: "true {{deg}}°",
    declination: "declination {{value}}",
    distance: "Distance",
    depth: "Depth",
    depthUnknown: "Depth unknown",
    slope: "Slant",
    back: "Way back",
    east: "E",
    west: "W",
    footnote:
      "Orientation aid from the recorded positions (± a few metres). Does not replace dive planning.",
  },
  menu: {
    open: "Menu",
    title: "Menu",
    close: "Close menu",
  },
  language: { label: "Language", de: "Deutsch", en: "English", fr: "Français", it: "Italiano" },
  search: {
    label: "Search dive spots or lakes",
    placeholder: "Search dive spots or lakes",
    clear: "Clear search",
    noResults: "No dive spots found",
    results_one: "{{count}} result",
    results_other: "{{count}} results",
  },
  lakeFilter: { label: "Filter by lake", all: "All lakes" },
  map: {
    controls: "Map controls",
    label: "Map of dive spots",
    layerLabel: "Map layer",
    layerMap: "Map",
    layerAerial: "Aerial",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    locate: "My location",
    locateDenied: "Location access was denied. Allow it in your browser settings.",
    locateUnavailable: "Location is not available.",
    locateOutside: "Your location is outside the map.",
    spotPin: "Dive spot {{name}}",
    spotPinClosed: "Dive spot {{name}}, closed",
    spotPinPermanent: "Dive spot {{name}}, closed for good",
    cluster: "{{count}} dive spots, tap to zoom in",
    loadError: "The dive spots could not be loaded.",
    attribution: "© swisstopo",
    scale: "Scale: {{distance}}",
  },
  wind: {
    toggle: "Wind",
    info: "About the wind forecast",
    offline: "Wind needs an internet connection",
    label: "Wind forecast",
    time: "Forecast time",
    now: "Now",
    model: "Model forecast, lake level",
    run: "Model run {{time}}",
    placeCenter: "Map centre",
    readout: "{{place}}: {{kmh}} km/h from {{dir}}",
    readoutNone: "{{place}}: no data",
    readoutCalm: "{{place}}: calm",
    dir: { n: "N", ne: "NE", e: "E", se: "SE", s: "S", sw: "SW", w: "W", nw: "NW" },
    creditShort: "Wind:",
    creditMeteo: "MeteoSwiss via",
    chip: "Model",
    creditVia: "Wind: MeteoSwiss ICON-CH via",
    interpolated: "interpolated",
    legend: "Wind speed in km/h",
    loading: "Loading wind …",
    unavailable: "Wind is temporarily unavailable.",
  },
  precip: {
    toggle: "Precipitation (rain and snow)",
    info: "About the precipitation forecast",
    offline: "Precipitation needs an internet connection",
    infoBoth: "About the wind and precipitation forecast",
    label: "Precipitation forecast",
    labelBoth: "Wind and precipitation forecast",
    legend: "Rain and snow",
    class0: "light",
    class1: "moderate",
    class2: "heavy",
    class3: "very heavy",
    hours: "{{from}}–{{to}}",
    readout: "{{place}}: Precipitation: {{hours}} {{mm}} mm/h",
    readoutDry: "{{place}}: {{hours}} no precipitation",
    readoutNone: "{{place}}: Precipitation: no data",
    model: "Model, not radar",
    creditShort: "Precipitation:",
    creditBoth: "Wind/Precipitation:",
    creditVia: "Precipitation: MeteoSwiss ICON-CH via",
    creditViaBoth: "Wind/Precipitation: MeteoSwiss ICON-CH via",
    loading: "Loading precipitation …",
    unavailable: "Precipitation is temporarily unavailable.",
  },
  sheet: {
    expandPanel: "Expand sidebar",
    collapsePanel: "Collapse sidebar",
    grabber: "Change panel height",
    detentPeek: "Small",
    detentMedium: "Medium",
    detentLarge: "Large",
    notFoundTitle: "Dive spot not found",
    notFoundText: "This dive spot does not exist or is not public.",
    backToMap: "Back to the map",
    loadError: "The dive spot could not be loaded.",
  },
  spot: {
    place: "{{locality}} · {{waterBody}}",
    view: "View",
    view2d: "2D",
    view3d: "3D",
    noBathy: "No lake-floor data",
    closedNowUntil: "Closed until {{date}}",
    closedNowReason: "Closed: {{reason}} until {{date}}",
    closedForGood: "Closed for good",
    closedForGoodSince: "Closed for good since {{date}}",
    closedForGoodReason: "Reason: {{reason}}",
    maxDepthBadge: "Max. {{value}}",
    objectCount_one: "{{count}} object",
    objectCount_other: "{{count}} objects",
  },
  difficulty: {
    very_easy: "Very easy",
    easy: "Easy",
    medium: "Medium",
    hard: "Hard",
    very_hard: "Very hard",
  },
  facts: {
    title: "Facts",
    difficulty: "Difficulty",
    maxDepth: "Max. depth",
    waterBody: "Water body",
    locality: "Place",
    waterLevel: "Water level",
    waterLevelValue: "{{value}} a.s.l.",
    entry: "Entry",
    parking: "Parking",
  },
  objects: {
    title: "Objects",
    unnamed: "Unnamed object",
    outside: "outside the tile — increase the radius",
    noData: "No data",
    approx: "approx.",
    provenance: "≈ calculated from the swissBATHY3D lake-floor model, ±0.5 m",
    type: {
      figure: "Figure",
      toilet: "Toilet",
      wreck: "Wreck",
      vehicle: "Vehicle",
      platform: "Platform",
      plaque: "Plaque",
      other: "Object",
    },
  },
  sections: {
    description: "Description",
    access: "Getting there",
    parking: "Parking",
    entry: "Entry",
    hazards: "Hazards",
    restrictions: "Restrictions",
    facilities: "Facilities",
  },
  admin: {
    brand: "Admin",
    menu: "Admin area",
    nav: "Admin navigation",
    nav_spots: "Dive spots",
    nav_lakes: "Lakes",
    nav_users: "Users",
    nav_settings: "Settings",
    nav_audit: "Activity log",
    nav_suggestions: "Suggestions",
    toMap: "To the map",
    edit: "Edit",
    signOut: "Sign out",
    signedInAs: "Signed in as {{name}}, {{role}}",
    common: {
      save: "Save",
      saved: "Saved.",
      cancel: "Cancel",
      delete: "Delete",
      edit: "Edit",
    },
    role: { admin: "Admin", moderator: "Moderator", diver: "Diver" },
    status: {
      all: "All",
      draft: "Draft",
      published: "Published",
      pending: "Pending",
    },
    login: {
      title: "Sign in",
      lead: "For moderators and admins only. An admin creates the accounts.",
      email: "Email",
      password: "Password",
      submit: "Sign in",
      missing: "Please enter your email and password.",
      invalid: "Email or password is wrong.",
      banned: "This account is banned.",
      rateLimited: "Too many attempts. Please try again later.",
      failed: "Sign-in failed. Please try again.",
      fixtures: "Sign-in needs the real API (not fixtures mode).",
    },
    forbidden: {
      staffOnly: "This area is for the team only.",
      title: "No access",
      body: "This page is for admins only.",
      back: "Back to dive spots",
    },
    discard: {
      title: "Discard changes?",
      body: "Your changes have not been saved and will be lost.",
      confirm: "Discard",
      keep: "Keep editing",
    },
    err: {
      concurrent: "Changed by someone else in the meantime — values updated.",
      load: "The data could not be loaded.",
      save: "That did not work. Please try again.",
      fix: "Please fix the marked fields.",
      auth: "Your session expired or you lack permission. Please sign in again.",
      required: "Required.",
      invalid: "Invalid value.",
      slug: "Lowercase letters, digits and single hyphens only.",
      slugTaken: "This slug is already taken.",
      coords: "Enter a valid coordinate (decimal degrees).",
      mmdd: "Use MM-DD, for example 11-15.",
      nameRequired: "Enter at least one translation.",
    },
    i18n: {
      language: "Text language",
      missing: "translation missing",
      missingHint: "Available in German, {{lang}} is still missing.",
    },
    spots: {
      title: "Dive spots",
      new: "New dive spot",
      create: "Create dive spot",
      edit: "Edit {{name}}",
      formLabel: "Dive spot form",
      filters: "Filters",
      search: "Search",
      count_one: "{{count}} dive spot",
      count_other: "{{count}} dive spots",
      drafts_one: "{{count}} draft",
      drafts_other: "{{count}} drafts",
      empty: "No dive spots for this filter.",
      open: "Open {{name}}",
      notFound: "Dive spot not found",
      backToList: "Back to the list",
      viewPublic: "Public page",
      publish: "Publish",
      unpublish: "Back to draft",
      delete: "Delete dive spot",
      deleteTitle: "Delete this dive spot?",
      deleteBody: "“{{name}}” and its objects disappear from the map. This cannot be undone here.",
      deleteConfirm: "Delete for good",
      draftNote: "This dive spot is a draft and invisible on the public map.",
    },
    section: {
      basics: "Basics",
      location: "Location",
      texts: "Texts",
      closed: "Closed periods",
      permanent: "Permanent closure",
      ratings: "Ratings",
      publication: "Publication",
    },
    field: {
      name: "Name",
      slug: "Slug (optional)",
      slugDerived: "Leave empty: it becomes “{{slug}}”.",
      slugHint: "Part of the address, for example /en/spot/…",
      waterBody: "Water body",
      locality: "Place",
      difficulty: "Difficulty",
      none: "Not specified",
      maxDepth: "Maximum depth (m)",
      status: "Status",
      entry: "Entry point",
      hasParking: "Add a parking location",
      parking: "Parking",
      lake: "Lake: {{name}}",
      noLake: "Not assigned to a lake",
      hasBathy: "with lake-floor data",
    },
    picker: {
      help: "Tap the map or drag the pin. Or type the coordinates.",
      lat: "Latitude (lat)",
      lon: "Longitude (lon)",
    },
    depth: {
      pick: "Pick a point to see the depth.",
      ok: "Depth from swissBATHY3D: ≈ {{depth}}",
      dry: "This point is above the water line.",
      noData: "No lake-floor data at this point.",
      noLake: "No lake with lake-floor data at this point.",
      error: "The depth could not be looked up.",
    },
    permanent: {
      toggle: "Closed for good",
      help: "For spots that can no longer be dived. They stay visible and are marked as closed.",
      since: "Since (YYYY-MM-DD, optional)",
      reason: "Reason",
      marker: "Closed for good",
    },
    closed: {
      help: "Repeats every year. From and to may span the new year, for example 11-15 to 02-28.",
      none: "No closed periods.",
      add: "Add a closed period",
      period: "Closed period {{n}}",
      from: "From (MM-DD)",
      to: "To (MM-DD)",
      reason: "Reason",
      remove: "Remove closed period {{n}}",
    },
    ratings: { clear: "Clear" },
    objects: {
      title: "Objects",
      add: "Add object",
      none: "No objects yet.",
      afterCreate: "You can add objects once the dive spot has been created.",
      addTitle: "New object",
      editTitle: "Edit object",
      edit: "Edit object {{name}}",
      delete: "Delete object {{name}}",
      deleteTitle: "Delete this object?",
      deleteBody: "“{{name}}” will be deleted.",
      type: "Type",
      name: "Name",
      description: "Description",
      location: "Object position",
      override: "Measured depth (m)",
      overrideHint: "Optional. Leave empty to use the computed depth (shown with ≈).",
    },
    lakes: {
      title: "Lakes",
      level: "Water level",
      levelInput: "Water level (m a.s.l.)",
      bathy: "with lake-floor data",
      edit: "Change the water level of {{name}}",
      invalid: "Enter a number between 0 and 5000.",
      warning: "Saving recomputes the depths of all objects in this lake.",
    },
    settings: {
      title: "Settings",
      registration: "Registration",
      allow: "Allow new registrations",
      max: "Max. new accounts per hour",
      maxHint: "0 = no new accounts. Applies to everyone together, in any hour.",
      maxInvalid: "Please enter a whole number from 0 to 1000.",
      newLastHour: "New accounts (last hour)",
      refusedLastHour: "Refused sign-ups (last hour)",
      noMail: "No email delivery is set up — registration cannot be opened",
      mailConflict: "Registration could not be opened: no email delivery is set up.",
      save: "Save",
      saved: "Saved.",
      updatedAt: "Last changed: {{date}}",
    },
    model: {
      title: "3D model",
      save: "Save model",
      none: "No 3D model uploaded.",
      choose: "Choose file",
      replace: "Replace model",
      file: ".glb file",
      drop: "Drop a .glb file here or choose one (up to {{max}} MB).",
      chosen: "{{name}} ({{size}}), not uploaded yet",
      attribution: "Source / licence",
      attributionHint: "Who made the model, and under which licence? Shown in dive mode.",
      scale: "Scale",
      heading: "Heading (°)",
      headingHint: "0 to below 360, the direction the front faces, clockwise from north.",
      discard: "Discard selection",
      discardBody: "Your changes to the 3D model have not been saved yet and will be lost.",
      headingShort: "Heading",
      upload: "Upload",
      remove: "Delete model",
      removeTitle: "Delete model?",
      removeBody: "This object's 3D model is removed. The object stays.",
      uploaded: "Model uploaded.",
      saved: "Saved.",
      deleted: "Model deleted.",
      size: "Size",
      triangles: "triangles",
      bounds: "Dimensions",
      animations: "Animations",
      preview: "3D model preview",
      previewLoading: "Loading preview …",
      previewFailed: "Preview not available.",
      north: "N",
      err: {
        extension: "Only .glb files (binary glTF) are allowed.",
        empty: "The file is empty.",
        size: "The file is larger than 8 MB.",
        magic: "This is not a valid .glb file.",
        tooLarge: "The file is too large (8 MB at most).",
        forbidden: "Only admins can change 3D models.",
        rateLimited: "Too many uploads. Please wait a moment.",
        notFound: "The model was removed in the meantime. Please reload.",
        scale: "Scale between 0.01 and 100.",
        heading: "Heading from 0 to below 360.",
      },
    },
    audit: {
      title: "Activity log",
      lead: "Who changed what, and when. Admins only.",
      filterEntity: "What",
      filterAction: "Action",
      all: "All",
      when: "Time",
      who: "Who",
      action: "Action",
      what: "What",
      script: "Script: {{label}}",
      deletedAccount: "Deleted account",
      deletedTarget: "deleted",
      unnamed: "unnamed",
      sugg: "Suggestion",
      details: "Changes ({{count}})",
      more: "more",
      less: "less",
      empty: "No entries found.",
      count_one: "{{count}} entry",
      count_other: "{{count}} entries",
      loadMore: "Load more",
      filterBy: "Show only {{name}}",
      removeFilter: "Remove filter: {{name}}",
      chipSpot: "Dive spot: {{name}}",
      chipActor: "By: {{name}}",
      unknownSpot: "Dive spot",
      unknownPerson: "Person",
      history: "History",
      yes: "yes",
      no: "no",
      adjusted: "Approved with adjustments",
      cascaded: "Deleted with the dive spot",
      redacted: "Personal data removed",
      reason: "Reason: {{text}}",
      kind: "Kind: {{kind}}",
      accountDeleted: "Account deleted",
      entity: {
        spot: "Dive spot",
        object: "Object",
        suggestion: "Suggestion",
        sighting: "Sighting",
        lake: "Lake",
        user: "User",
        settings: "Settings",
      },
      actions: {
        create: "created",
        update: "edited",
        delete: "deleted",
        submit: "suggested",
        withdraw: "withdrawn",
        approve: "approved",
        reject: "rejected",
      },
      field: {
        name: "Name",
        slug: "Slug",
        location: "Location",
        waterBody: "Water body",
        locality: "Locality",
        difficulty: "Difficulty",
        maxDepthM: "Maximum depth (m)",
        closedPeriods: "Closed periods",
        permanentClosure: "Permanent closure",
        status: "Status",
        parkingLocation: "Parking location",
        description: "Description",
        access: "Access",
        parking: "Parking",
        entry: "Entry",
        hazards: "Hazards",
        restrictions: "Restrictions",
        facilities: "Facilities",
        ratings: "Ratings",
        ratings_fish: "Rating fish",
        ratings_walls: "Rating walls",
        ratings_caves: "Rating caves",
        ratings_wrecksSculptures: "Rating wrecks and sculptures",
        ratings_current: "Rating current",
        ratings_surroundings: "Rating surroundings",
        type: "Type",
        depthOverrideM: "Depth, manual (m)",
        model: "3D model",
        model_scale: "3D model: scale",
        model_headingDeg: "3D model: heading (°)",
        model_modelId: "3D model: file",
        waterLevelM: "Water level (m a.s.l.)",
        hasBathy: "With lakebed data",
        registrationOpen: "Registration open",
        maxNewAccountsPerHour: "New accounts per hour (max.)",
        diveAccess: "Dive mode",
        banned: "Blocked",
        role: "Role",
        password: "Password",
        present: "Still there",
      },
      countMore_one: "{{count}} latest entry · more available",
      countMore_other: "{{count}} latest entries · more available",
      fieldLabel: "{{label}}:",
      activeFilters: "Active filters",
    },
    users: {
      search: "Search name or email",
      filterRole: "Role",
      filterStatus: "Status",
      all: "All",
      active: "Active",
      blocked: "Blocked",
      new24h: "New in the last 24 h",
      email: "Email",
      registered: "Registered",
      lastSeen: "Last seen",
      never: "never",
      pending: "Pending suggestions",
      user: "User",
      pendingShort: "Open",
      diveModeShort: "incl.",
      diveMode: "Dive mode",
      diveModeOf: "Dive mode for {{name}}",
      diveModeAdmin: "Included with admin",
      empty: "No users found.",
      count: "{{count}} users",
      banTitle: "Block {{name}}?",
      banBody: "{{name}} is signed out everywhere at once and cannot sign in any more.",
      unbanTitle: "Unblock {{name}}?",
      unbanBody: "{{name}} can sign in again.",
      title: "Users",
      you: "you",
      role: "Role",
      roleOf: "Role of {{name}}",
      ban: "Ban",
      unban: "Unban",
      banOf: "Ban {{name}}",
      unbanOf: "Unban {{name}}",
      banned: "Banned",
      selfNote: "Your own role and ban can't be changed.",
      selfConflict: "That is not possible for your own account.",
      create: "Create user",
      createSubmit: "Create user",
      created: "{{email}} was created.",
      name: "Name",
      initialPassword: "Initial password",
      passwordHint: "At least 12 characters. Hand it over in person, not by email or chat.",
      passwordShort: "At least 12 characters.",
      emailInvalid: "Enter a valid email address.",
      emailTaken: "This email address is already in use.",
    },
  },
  three: {
    shoreLeft: "Along the shore, left",
    shoreRight: "Along the shore, right",
    backToEntry: "Back to the entry",
    lakeEnd: "End of the lake reached",
    shoreLost: "Shore not found",
    spotPin: "Dive spot {{name}}",
    canvas:
      "3D model of the lake floor. Drag to rotate, tap to read the depth. Use the arrow keys to move a crosshair over the lake floor.",
    back2d: "2D view",
    radius: "Radius",
    exaggeration: "Exaggeration",
    exaggerationValue: "{{value}}×",
    water: "Water surface",
    planes: "Depth floors",
    compassNow: "Compass, looking {{deg}} degrees {{name}}",
    cardinal: { N: "N", NE: "NE", E: "E", SE: "SE", S: "S", SW: "SW", W: "W", NW: "NW" },
    cardinalName: {
      N: "north",
      NE: "northeast",
      E: "east",
      SE: "southeast",
      S: "south",
      SW: "southwest",
      W: "west",
      NW: "northwest",
    },
    compass: "Compass, tap to return to the default view",
    loading: "Loading the lake floor …",
    noDataHere: "No lake-floor data here",
    rateLimited: "Too many requests. Please try again later.",
    loadFailed: "The lake floor could not be loaded.",
    noWebgl: "Your browser cannot display 3D graphics (WebGL).",
    contextLost: "The 3D graphics were interrupted.",
    reload: "Reload",
    legend: "Depth",
    readoutNote: "swissBATHY3D, ±0.5 m",
    readoutNoData: "No data",
    readoutLand: "Shore",
    pinLabel: "Object {{name}}, {{depth}}",
    place: {
      toggle: "Place object",
      hint: "Tap the lakebed to place an object.",
      movingHint: "Tap the lakebed: destination for “{{name}}”.",
      cancel: "Cancel",
      move: "Move",
      moveConfirmTitle: "Move “{{name}}”?",
      moveConfirmBody: "“{{name}}” moves to the tapped spot. Its depth is recomputed.",
      moveConfirm: "Move",
      depthPreview: "Computed depth: {{depth}} (preview)",
      noDepth: "No depth data – enter the depth manually.",
      panel: "Place object",
    },
    objectClose: "Close object details",
  },
  ratings: {
    title: "Ratings",
    fish: "Fish",
    walls: "Walls",
    caves: "Caves",
    wrecksSculptures: "Wrecks and sculptures",
    current: "Current",
    surroundings: "Surroundings",
    value: "{{label}}: {{value}} out of 5",
  },
};
